import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { findNearestLocation, ServiceLocation } from '@/lib/locationUtils';

interface LocationContextType {
  currentLocation: ServiceLocation | null;
  allLocations: ServiceLocation[];
  setLocation: (location: ServiceLocation | null) => void;
  loading: boolean;
  detectLocation: () => void;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export function LocationProvider({ children }: { children: ReactNode }) {
  const [currentLocation, setCurrentLocation] = useState<ServiceLocation | null>(null);
  const [allLocations, setAllLocations] = useState<ServiceLocation[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch all service locations
  useEffect(() => {
    const fetchLocations = async () => {
      const { data, error } = await supabase
        .from('service_locations')
        .select('*')
        .eq('is_active', true)
        .order('display_order');

      if (data && !error) {
        setAllLocations(data as ServiceLocation[]);
      }
    };

    fetchLocations();
  }, []);

  // Restore location from localStorage only — NO auto-geolocation
  useEffect(() => {
    if (allLocations.length === 0) return;

    // Check for cached location with 24-hour expiry
    const cachedLocation = localStorage.getItem('cached_location');
    const cacheTimestamp = localStorage.getItem('cached_location_timestamp');
    
    if (cachedLocation && cacheTimestamp) {
      const timestamp = parseInt(cacheTimestamp, 10);
      const hoursSinceCache = (Date.now() - timestamp) / (1000 * 60 * 60);
      
      if (hoursSinceCache < 24) {
        const cached = JSON.parse(cachedLocation);
        const cachedLoc = allLocations.find((loc) => loc.slug === cached.slug);
        if (cachedLoc) {
          setCurrentLocation(cachedLoc);
          setLoading(false);
          return;
        }
      }
    }

    // Check for manual preference (no expiry)
    const storedLocationSlug = localStorage.getItem('preferred_location');
    if (storedLocationSlug) {
      const stored = allLocations.find((loc) => loc.slug === storedLocationSlug);
      if (stored) {
        setCurrentLocation(stored);
        setLoading(false);
        localStorage.setItem('cached_location', JSON.stringify(stored));
        localStorage.setItem('cached_location_timestamp', Date.now().toString());
        return;
      }
    }

    // No location — leave as null (no default, no auto-detection)
    setLoading(false);
  }, [allLocations]);

  const setLocation = (location: ServiceLocation | null) => {
    setCurrentLocation(location);
    if (location) {
      localStorage.setItem('preferred_location', location.slug);
      localStorage.setItem('cached_location', JSON.stringify(location));
      localStorage.setItem('cached_location_timestamp', Date.now().toString());
    } else {
      localStorage.removeItem('preferred_location');
      localStorage.removeItem('cached_location');
      localStorage.removeItem('cached_location_timestamp');
    }
  };

  // Opt-in geolocation: only runs when user clicks "Use My Location"
  const detectLocation = useCallback(() => {
    if (!navigator.geolocation || allLocations.length === 0) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const nearest = findNearestLocation(
          position.coords.latitude,
          position.coords.longitude,
          allLocations
        );
        if (nearest) {
          setLocation(nearest);
        }
      },
      () => {
        // Silently fail — user denied or error
      },
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
    );
  }, [allLocations]);

  return (
    <LocationContext.Provider
      value={{ currentLocation, allLocations, setLocation, loading, detectLocation }}
    >
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation() {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
}
